import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaBolt,
  FaBox,
  FaBuilding,
  FaClipboardCheck,
  FaClipboardList,
  FaCloudUploadAlt,
  FaFileAlt,
  FaGlobe,
  FaMapMarkerAlt,
  FaMapPin,
  FaPaperclip,
  FaPaperPlane,
  FaShieldAlt,
  FaShoppingBag,
  FaSortNumericUp,
  FaStar,
  FaTag,
  FaTags,
  FaTh,
  FaThumbtack,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { buildLoginPath } from "../../utils/authRedirect";
import { BUYER_RFQ_POST_PATH } from "../../utils/buyerRfqRedirect";
import { saveQuickBuyRequestDraft } from "../../utils/quickBuyRequestDraft";
import { useProductDetailsMaster } from "../../hooks/useProductDetailsMaster";
import { useLocationMaster } from "../../hooks/useLocationMaster";

const MAX_UPLOADS = 3;

const PREFERENCES_OPTIONS = [
  { value: "", label: "--Select--" },
  { value: "buy-directly", label: "Buy directly by posting my needs on the platform" },
  { value: "buy-with-help", label: "Buy with help from the platform's procurement team" },
];

const QUICK_RFQ_FEATURES = [
  { icon: FaClipboardCheck, title: "Quick & Easy", desc: "Fill few details" },
  { icon: FaShieldAlt, title: "Better Responses", desc: "Get competitive quotes" },
  { icon: FaShoppingBag, title: "Save Time", desc: "Connect with verified suppliers" },
];

const EMPTY_FORM = {
  productName: "",
  productCategory: "",
  productSubCategory: "",
  productSubSubCategory: "",
  quantity: "",
  unit: "",
  shippingAddress: "",
  shippingCountry: "",
  shippingState: "",
  shippingCity: "",
  shippingPincode: "",
  preferences: "",
  description: "",
};

function mapCategoryOptions(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const id = Number(row.category_id ?? row.id);
      const name = String(row.category ?? row.category_name ?? row.name ?? "").trim();
      return id > 0 && name ? { value: String(id), label: name } : null;
    })
    .filter(Boolean);
}

function mapSubcategoryOptions(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const id = Number(row.sub_category_id ?? row.id);
      const name = String(row.sub_category ?? row.sub_category_name ?? row.name ?? "").trim();
      return id > 0 && name ? { value: String(id), label: name } : null;
    })
    .filter(Boolean);
}

function mapSubSubcategoryOptions(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const id = Number(row.sub_sub_category_id ?? row.id);
      const name = String(row.sub_sub_category ?? row.sub_sub_category_name ?? row.name ?? "").trim();
      return id > 0 && name ? { value: String(id), label: name } : null;
    })
    .filter(Boolean);
}

function mapLocationOptions(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const id = row.id ?? row.country_id ?? row.state_id ?? row.city_id;
      const label = String(row.label ?? row.country_name ?? row.state_name ?? row.city_name ?? row.name ?? "").trim();
      return id != null && label ? { value: String(id), label } : null;
    })
    .filter(Boolean);
}

function findLocationOptionValue(options, label) {
  if (!label) return "";
  const match = options.find((option) => option.label === label);
  return match?.value || "";
}

function extractListRows(response) {
  const payload = response?.data ?? response ?? {};
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

async function uploadQuickBuyFile(file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", "product");
  formData.append("companyName", "RFQ");
  formData.append("fileType", "product_image");

  const response = await CommonService.post("/upload/single", formData);
  if (response?.success && response.data) {
    return response.data.accessPath || response.data.filePath || "";
  }
  throw new Error(response?.message || "Upload failed");
}

function FormField({ label, htmlFor, icon: Icon, iconTone = "slate", children, className = "" }) {
  return (
    <label className={`hub-quick-buy__control ${className}`.trim()} htmlFor={htmlFor}>
      <span className={`hub-quick-buy__label hub-quick-buy__label--${iconTone}`}>
        {Icon ? <Icon className="hub-quick-buy__label-icon" aria-hidden /> : null}
        <span>{label}</span>
      </span>
      {children}
    </label>
  );
}

function SectionLabel({ icon: Icon, children, iconTone = "blue" }) {
  return (
    <span className={`hub-quick-buy__section-label hub-quick-buy__section-label--${iconTone}`}>
      {Icon ? <Icon className="hub-quick-buy__section-label-icon" aria-hidden /> : null}
      <span>{children}</span>
    </span>
  );
}

function QuickBuyRequestForm({
  categories,
  form,
  setForm,
  uploadedFiles,
  setUploadedFiles,
  submitting,
  onSubmit,
}) {
  const fileInputRef = useRef(null);
  const { attributeUnitOptions } = useProductDetailsMaster();
  const {
    countries,
    states,
    cities,
    loading: loadingCountries,
    loadingStates,
    loadingCities,
    updateListsForCountry,
    updateListsForState,
  } = useLocationMaster();
  const [subCategories, setSubCategories] = useState([]);
  const [subSubCategories, setSubSubCategories] = useState([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [loadingSubSubCategories, setLoadingSubSubCategories] = useState(false);

  const categoryOptions = useMemo(() => mapCategoryOptions(categories), [categories]);
  const subCategoryOptions = useMemo(() => mapSubcategoryOptions(subCategories), [subCategories]);
  const subSubCategoryOptions = useMemo(() => mapSubSubcategoryOptions(subSubCategories), [subSubCategories]);
  const unitOptions = useMemo(
    () =>
      (attributeUnitOptions || []).map((option) => ({
        value: String(option.value ?? option.label ?? ""),
        label: String(option.label ?? option.value ?? ""),
      })),
    [attributeUnitOptions],
  );
  const countryOptions = useMemo(() => mapLocationOptions(countries), [countries]);
  const stateOptions = useMemo(() => mapLocationOptions(states), [states]);
  const cityOptions = useMemo(() => mapLocationOptions(cities), [cities]);

  useEffect(() => {
    if (!form.unit && unitOptions.length) {
      setForm((prev) => ({ ...prev, unit: unitOptions[0].value }));
    }
  }, [form.unit, unitOptions, setForm]);

  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!form.productCategory) {
        setSubCategories([]);
        setSubSubCategories([]);
        return;
      }

      setLoadingSubCategories(true);
      try {
        const response = await CommonService.get(
          `/master/master-sub-categories/${form.productCategory}`,
          {},
          { silent: true },
        );
        setSubCategories(extractListRows(response));
        setSubSubCategories([]);
      } catch {
        setSubCategories([]);
        setSubSubCategories([]);
      } finally {
        setLoadingSubCategories(false);
      }
    };

    void fetchSubCategories();
  }, [form.productCategory]);

  useEffect(() => {
    const fetchSubSubCategories = async () => {
      if (!form.productSubCategory) {
        setSubSubCategories([]);
        return;
      }

      setLoadingSubSubCategories(true);
      try {
        const response = await CommonService.get(
          `/master/master-sub-sub-categories/${form.productSubCategory}`,
          {},
          { silent: true },
        );
        setSubSubCategories(extractListRows(response));
      } catch {
        setSubSubCategories([]);
      } finally {
        setLoadingSubSubCategories(false);
      }
    };

    void fetchSubSubCategories();
  }, [form.productSubCategory]);

  const updateField = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "productCategory") {
        next.productSubCategory = "";
        next.productSubSubCategory = "";
      }
      if (name === "productSubCategory") {
        next.productSubSubCategory = "";
      }
      return next;
    });
  };

  const handleCountryChange = (countryId) => {
    const selected = countryOptions.find((option) => option.value === countryId);
    setForm((prev) => ({
      ...prev,
      shippingCountry: selected?.label || "",
      shippingState: "",
      shippingCity: "",
    }));
    void updateListsForCountry(countryId || null);
  };

  const handleStateChange = (stateId) => {
    const selected = stateOptions.find((option) => option.value === stateId);
    const countryId = findLocationOptionValue(countryOptions, form.shippingCountry);
    setForm((prev) => ({
      ...prev,
      shippingState: selected?.label || "",
      shippingCity: "",
    }));
    if (stateId && countryId) {
      void updateListsForState(countryId, stateId);
    } else {
      void updateListsForState(null, null);
    }
  };

  const handleCityChange = (cityId) => {
    const selected = cityOptions.find((option) => option.value === cityId);
    updateField("shippingCity", selected?.label || "");
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const remaining = MAX_UPLOADS - uploadedFiles.length;
    if (remaining <= 0) {
      toast.warn(`You can upload up to ${MAX_UPLOADS} files.`);
      return;
    }

    setUploadedFiles((prev) => [...prev, ...files.slice(0, remaining)]);
    event.target.value = "";
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  return (
    <form className="hub-quick-buy__form hub-quick-buy__form--compact" onSubmit={onSubmit} noValidate>
      <div className="hub-quick-buy__form-head">
        <div className="hub-quick-buy__form-head-copy">
          <div className="hub-quick-buy__form-title-row">
            <span className="hub-quick-buy__form-title-icon" aria-hidden>
              <FaClipboardList />
            </span>
            <div>
              <h3 id="hub-quick-rfq-modal-title" className="hub-quick-buy__form-title">
                Submit Quick RFQ
              </h3>
              <p className="hub-quick-buy__form-lead">
                Fill in the details below to request a quick quote from our suppliers.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="hub-quick-buy__form-body">
        <div className="hub-quick-buy__product-row">
          <FormField label="Product Name" htmlFor="hub-qb-product-name" icon={FaBox} iconTone="orange">
            <input
              id="hub-qb-product-name"
              type="text"
              className="hub-quick-buy__input"
              placeholder="Enter the product name"
              value={form.productName}
              onChange={(event) => updateField("productName", event.target.value)}
            />
          </FormField>
          <FormField label="Product Quantity" htmlFor="hub-qb-quantity" icon={FaSortNumericUp} iconTone="purple">
            <input
              id="hub-qb-quantity"
              type="number"
              min="0"
              step="any"
              className="hub-quick-buy__input"
              placeholder="Enter quantity"
              value={form.quantity}
              onChange={(event) => updateField("quantity", event.target.value)}
            />
          </FormField>
          <FormField label="Unit" htmlFor="hub-qb-unit" icon={FaTh} iconTone="green">
            <select
              id="hub-qb-unit"
              className="hub-quick-buy__input hub-quick-buy__select"
              value={form.unit}
              onChange={(event) => updateField("unit", event.target.value)}
            >
              <option value="">--Select--</option>
              {unitOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        <div className="hub-quick-buy__section">
          <SectionLabel icon={FaThumbtack}>Product Category</SectionLabel>
          <div className="hub-quick-buy__category-grid">
            <FormField label="Category" htmlFor="hub-qb-category" icon={FaTag} iconTone="red">
              <select
                id="hub-qb-category"
                className="hub-quick-buy__input hub-quick-buy__select"
                value={form.productCategory}
                onChange={(event) => updateField("productCategory", event.target.value)}
              >
                <option value="">Select category</option>
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Sub Category" htmlFor="hub-qb-sub-category" icon={FaFileAlt} iconTone="blue">
              <select
                id="hub-qb-sub-category"
                className="hub-quick-buy__input hub-quick-buy__select"
                value={form.productSubCategory}
                onChange={(event) => updateField("productSubCategory", event.target.value)}
                disabled={!form.productCategory || loadingSubCategories}
              >
                <option value="">
                  {!form.productCategory
                    ? "Select category first"
                    : loadingSubCategories
                      ? "Loading…"
                      : "Select sub category"}
                </option>
                {subCategoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="Sub Sub Category" htmlFor="hub-qb-sub-sub-category" icon={FaTags} iconTone="yellow">
              <select
                id="hub-qb-sub-sub-category"
                className="hub-quick-buy__input hub-quick-buy__select"
                value={form.productSubSubCategory}
                onChange={(event) => updateField("productSubSubCategory", event.target.value)}
                disabled={!form.productSubCategory || loadingSubSubCategories}
              >
                <option value="">
                  {!form.productSubCategory
                    ? "Select sub category first"
                    : loadingSubSubCategories
                      ? "Loading…"
                      : subSubCategoryOptions.length
                        ? "Select sub sub category"
                        : "No sub sub categories"}
                </option>
                {subSubCategoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        </div>

        <div className="hub-quick-buy__section">
          <SectionLabel icon={FaMapMarkerAlt}>Place of Delivery</SectionLabel>
          <div className="hub-quick-buy__address-row">
            <FormField label="Address" htmlFor="hub-qb-address" icon={FaMapPin} iconTone="green">
              <input
                id="hub-qb-address"
                type="text"
                className="hub-quick-buy__input"
                placeholder="Enter delivery address"
                value={form.shippingAddress}
                onChange={(event) => updateField("shippingAddress", event.target.value)}
              />
            </FormField>
            <FormField label="Pincode" htmlFor="hub-qb-pincode" icon={FaUser} iconTone="purple">
              <input
                id="hub-qb-pincode"
                type="text"
                className="hub-quick-buy__input"
                placeholder="Pincode"
                maxLength={10}
                value={form.shippingPincode}
                onChange={(event) => updateField("shippingPincode", event.target.value)}
              />
            </FormField>
          </div>
          <div className="hub-quick-buy__delivery-grid">
            <FormField label="Country" htmlFor="hub-qb-country" icon={FaGlobe} iconTone="purple">
              <select
                id="hub-qb-country"
                className="hub-quick-buy__input hub-quick-buy__select"
                value={findLocationOptionValue(countryOptions, form.shippingCountry)}
                onChange={(event) => handleCountryChange(event.target.value)}
                disabled={loadingCountries}
              >
                <option value="">{loadingCountries ? "Loading…" : "Select country"}</option>
                {countryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="State" htmlFor="hub-qb-state" icon={FaFileAlt} iconTone="blue">
              <select
                id="hub-qb-state"
                className="hub-quick-buy__input hub-quick-buy__select"
                value={findLocationOptionValue(stateOptions, form.shippingState)}
                onChange={(event) => handleStateChange(event.target.value)}
                disabled={!form.shippingCountry || loadingStates}
              >
                <option value="">
                  {!form.shippingCountry
                    ? "Select country first"
                    : loadingStates
                      ? "Loading…"
                      : "Select state"}
                </option>
                {stateOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label="City" htmlFor="hub-qb-city" icon={FaBuilding} iconTone="teal">
              <select
                id="hub-qb-city"
                className="hub-quick-buy__input hub-quick-buy__select"
                value={findLocationOptionValue(cityOptions, form.shippingCity)}
                onChange={(event) => handleCityChange(event.target.value)}
                disabled={!form.shippingState || loadingCities}
              >
                <option value="">
                  {!form.shippingState
                    ? "Select state first"
                    : loadingCities
                      ? "Loading…"
                      : "Select city"}
                </option>
                {cityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        </div>

        <FormField
          label="Choose Your Preferences"
          htmlFor="hub-qb-preferences"
          icon={FaStar}
          iconTone="orange"
          className="hub-quick-buy__control--inline"
        >
          <select
            id="hub-qb-preferences"
            className="hub-quick-buy__input hub-quick-buy__select"
            value={form.preferences}
            onChange={(event) => updateField("preferences", event.target.value)}
          >
            {PREFERENCES_OPTIONS.map((option) => (
              <option key={option.value || "default"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FormField>

        <div className="hub-quick-buy__desc-upload-row">
          <FormField
            label="Product Description"
            htmlFor="hub-qb-description"
            icon={FaFileAlt}
            iconTone="green"
            className="hub-quick-buy__control--textarea"
          >
            <textarea
              id="hub-qb-description"
              className="hub-quick-buy__textarea"
              rows={2}
              placeholder="Specifications, quality, packaging, or delivery timeline"
              value={form.description}
              onChange={(event) => updateField("description", event.target.value)}
            />
          </FormField>
          <div className="hub-quick-buy__upload-panel">
            <span className="hub-quick-buy__upload-label hub-quick-buy__upload-label--purple">
              <FaPaperclip className="hub-quick-buy__label-icon" aria-hidden />
              <span>Attachments</span>
            </span>
            <button
              type="button"
              className="hub-quick-buy__upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadedFiles.length >= MAX_UPLOADS || submitting}
            >
              <FaCloudUploadAlt aria-hidden />
              <span>
                Upload ({uploadedFiles.length}/{MAX_UPLOADS})
              </span>
            </button>
            <p className="hub-quick-buy__upload-hint">Upload up to 3 files (PDF, JPG, PNG)</p>
            <input
              ref={fileInputRef}
              type="file"
              className="hub-quick-buy__file-input"
              accept="image/*,video/*"
              multiple
              onChange={handleFileChange}
            />
            {uploadedFiles.length ? (
              <ul className="hub-quick-buy__file-list">
                {uploadedFiles.map((file, index) => (
                  <li key={`${file.name}-${index}`} className="hub-quick-buy__file-chip">
                    <span className="hub-quick-buy__file-chip-name">{file.name}</span>
                    <button
                      type="button"
                      className="hub-quick-buy__file-chip-remove"
                      onClick={() => removeUploadedFile(index)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <FaTimes aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </div>
      </div>

      <div className="hub-quick-buy__actions">
        <button type="submit" className="hub-quick-buy__submit" disabled={submitting}>
          <FaPaperPlane aria-hidden />
          <span>{submitting ? "Sending…" : "Send Request"}</span>
        </button>
      </div>
    </form>
  );
}

export default function HubQuickBuyRequestSection({ categories = [] }) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!modalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) {
        setModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalOpen, submitting]);

  const validateForm = () => {
    const missing = [];
    if (!form.productName.trim()) missing.push("Product Name");
    if (!form.productCategory) missing.push("Category");
    if (!form.quantity.trim()) missing.push("Product Quantity");
    if (!form.unit) missing.push("Unit");
    if (!form.shippingAddress.trim()) missing.push("Address");
    if (!form.shippingCountry) missing.push("Country");
    if (!form.shippingState) missing.push("State");
    if (!form.shippingCity) missing.push("City");
    if (!form.shippingPincode.trim()) missing.push("Pincode");
    if (!form.preferences) missing.push("Choose Your Preferences");
    if (!form.description.trim()) missing.push("Product Description");
    return missing;
  };

  const buildDraft = () => ({
    productName: form.productName.trim(),
    productCategory: form.productCategory,
    productSubCategory: form.productSubCategory,
    productSubSubCategory: form.productSubSubCategory,
    quantity: form.quantity.trim(),
    unit: form.unit,
    shippingAddress: form.shippingAddress.trim(),
    shippingCountry: form.shippingCountry,
    shippingState: form.shippingState,
    shippingCity: form.shippingCity,
    shippingPincode: form.shippingPincode.trim(),
    preferences: form.preferences,
    description: form.description.trim(),
  });

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const missing = validateForm();
    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(", ")}`);
      return;
    }

    const draft = buildDraft();

    if (!StorageService.isAuthenticated()) {
      saveQuickBuyRequestDraft(draft);
      setModalOpen(false);
      navigate(buildLoginPath(`${BUYER_RFQ_POST_PATH}?fromQuickBuy=1`));
      return;
    }

    setSubmitting(true);
    try {
      const uploadedUrls = [];
      for (const file of uploadedFiles) {
        try {
          const url = await uploadQuickBuyFile(file);
          if (url) uploadedUrls.push(url);
        } catch {
          toast.error(`Failed to upload ${file.name}.`);
        }
      }

      const payload = {
        product_name: draft.productName,
        category_id: draft.productCategory ? parseInt(draft.productCategory, 10) : null,
        sub_category_id: draft.productSubCategory ? parseInt(draft.productSubCategory, 10) : null,
        sub_sub_category_id: draft.productSubSubCategory ? parseInt(draft.productSubSubCategory, 10) : null,
        product_quantity: draft.quantity ? parseFloat(draft.quantity) : null,
        quantity_unit: draft.unit || null,
        shipping_address: draft.shippingAddress,
        shipping_country: draft.shippingCountry,
        shipping_state: draft.shippingState,
        shipping_city: draft.shippingCity,
        shipping_pincode: draft.shippingPincode,
        preferences: draft.preferences,
        product_description: draft.description,
        uploaded_files: uploadedUrls,
        status: "pending",
      };

      const response = await CommonService.post("/rfq", payload);
      if (response?.success) {
        toast.success("Your quick RFQ was sent successfully.");
        setForm(EMPTY_FORM);
        setUploadedFiles([]);
        setModalOpen(false);
        navigate("/buyer-rfq");
        return;
      }

      throw new Error(response?.message || "Could not send request.");
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0] ||
        error?.message ||
        "Could not send request.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const modal = modalOpen
    ? createPortal(
        <div className="hub-quick-buy__modal-backdrop" role="presentation" onClick={closeModal}>
          <div
            className="hub-quick-buy__modal hub-quick-buy__modal--compact"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hub-quick-rfq-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button type="button" className="hub-quick-buy__modal-close" onClick={closeModal} aria-label="Close">
              <FaTimes aria-hidden />
            </button>
            <QuickBuyRequestForm
              categories={categories}
              form={form}
              setForm={setForm}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              submitting={submitting}
              onSubmit={handleSubmit}
            />
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <section className="hub-section hub-quick-buy" aria-labelledby="hub-quick-rfq-title">
      <div className="hub-quick-buy__banner">
        <div className="hub-quick-buy__intro">
          <span className="hub-quick-buy__icon" aria-hidden>
            <FaBolt />
          </span>
          <div>
            <h2 id="hub-quick-rfq-title" className="hub-quick-buy__title">
              Quick RFQ
            </h2>
            <p className="hub-quick-buy__subtitle">
              Submit your requirement in seconds and receive quotes faster
            </p>
          </div>
        </div>

        <div className="hub-quick-buy__features" aria-label="Quick RFQ benefits">
          {QUICK_RFQ_FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div key={feature.title} className="hub-quick-buy__feature">
                <span className="hub-quick-buy__feature-icon" aria-hidden>
                  <Icon />
                </span>
                <div className="hub-quick-buy__feature-copy">
                  <strong>{feature.title}</strong>
                  <span>{feature.desc}</span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="hub-quick-buy__cta">
          <button type="button" className="hub-quick-buy__cta-btn" onClick={() => setModalOpen(true)}>
            <span>Submit RFQ</span>
            <FaArrowRight aria-hidden />
          </button>
        </div>
      </div>

      {modal}
    </section>
  );
}
